import { ApplicationVariant, ErrorCodes } from "./ea.enums";

export const LAST_MOVEMENT_EXPIRATION_TIME = 1000 * 60 * 20;
export const DEFAULT_LOCALE = "en";
export const PROJECT_DEFAULT_PATH = "root";

export const FORMDATA_FIELD_NAMES = {
  datasources: "datasources[]",
  translation: "translation",
  stepFiles: "stepFiles[]",
};

export const DEFAULT_SCREENSHOT_INTERVAL_MINUTES = 24 * 60;
export const FILE_EXTENSION = {
  DOCX: ".docx",
  EA: ".ea",
  EA_CODE_TEMPLATE: ".eaCodeTemplate",
  JSON: ".json",
  PDF: ".pdf",
  XLS: ".xls",
  XLSX: ".xlsx",
  ZIP: ".zip",
  AXTR: ".axtr",
  XML: ".xml",
};
export const FILE_FORMAT = {
  scriptExport: { "application/octet-stream": [FILE_EXTENSION.EA] },
  codeTemplate: { "application/octet-stream": [FILE_EXTENSION.EA_CODE_TEMPLATE] },
  datasource: {
    "application/vnd.ms-excel": [FILE_EXTENSION.XLS],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [FILE_EXTENSION.XLSX],
  },
  json: { "application/json": [FILE_EXTENSION.JSON] },
  documents: {
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      FILE_EXTENSION.DOCX,
    ],
  },
  rsat: {
    "application/octet-stream": [FILE_EXTENSION.AXTR],
    "text/xml": [FILE_EXTENSION.XML],
    "application/xml": [FILE_EXTENSION.XML],
  },
};

export const PASSWORD_SECURITY_PLACEHOLDER = "***";

export const MAX_MAXIMUM_PARALLEL_SESSIONS = 999999;
export const RUNNER_FRAME_ID = "EA_RUNNER_FRAME";
export const CodeTemplatesFunctionName = "CodeTemplates" as const;
export const PredefinedVariablesContainerName = "Predefined";

export const AUDIT_TRAIL_MODE_ERROR = "AUDIT_TRAIL_MODE_ERROR";

export type ErrorCodeDetails = {
  friendlyError: string;
  solutions: string[];
  reasons: string[];
};

export const ERROR_CODE_DETAILS = {
  [ErrorCodes.MISSING_COMMAND]: {
    friendlyError: "The expected platform was not loaded, so the command could not be executed.",
    reasons: [
      "The step requires a platform that was not loaded during execution.",
      "The selected environment was unavailable or not running correctly.",
      "The script was executed with a different platform than the one required by this step.",
    ],
    solutions: [
      "Verify that the environment is up and running.",
      "Make sure the script is using the correct environment and platform.",
    ],
  },
  [ErrorCodes.MISSING_FRAME_OBJECT]: {
    friendlyError: "Unable to resolve the frame for this step.",
    reasons: [
      "The step references a frame, but the recorded frame metadata is missing.",
      "The page structure changed since the step was recorded.",
    ],
    solutions: [
      "Re-record the step after the target frame is visible.",
      "Check whether the application opens the content in a different frame or tab.",
    ],
  },
  [ErrorCodes.FRAME_NOT_FOUND]: {
    friendlyError: "Unable to find the expected frame on the page.",
    reasons: [
      "The target frame was not loaded when the step started.",
      "More than one frame matched the recorded frame metadata.",
    ],
    solutions: [
      "Add a wait before this step if the frame loads asynchronously.",
      "Re-record the step if the application layout changed.",
    ],
  },
  [ErrorCodes.PAGE_CONTEXT_NOT_FOUND]: {
    friendlyError: "The runner lost the page context for this frame.",
    reasons: [
      "The frame was detached or navigated while the step was running.",
      "The runner attempted to use context that was already disposed.",
    ],
    solutions: [
      "Retry the execution to rule out a transient navigation issue.",
      "Add a wait or split the step if the previous action triggers navigation.",
    ],
  },
  [ErrorCodes.AUTH_NOT_SUPPORTED_IN_FRAME]: {
    friendlyError: "Authentication cannot be applied inside a frame.",
    reasons: [
      "The selected authentication step was executed in an iframe context.",
      "Cookie or credential authentication requires the main page context.",
    ],
    solutions: [
      "Move authentication to a step that runs on the main page.",
      "Check whether the script navigated into a frame before authentication was requested.",
    ],
  },
  [ErrorCodes.MISSING_AI_EXTRACTION_INSTRUCTION]: {
    friendlyError: "AI extraction is missing an instruction.",
    reasons: [
      "The AI extraction step has no instruction configured.",
      "The step definition was saved without the required extraction prompt.",
    ],
    solutions: [
      "Open the step and add an AI extraction instruction.",
      "Re-save or re-record the AI extraction step.",
    ],
  },
  [ErrorCodes.AI_EXTRACTION_FAILED]: {
    friendlyError: "AI extraction failed.",
    reasons: [
      "The model could not extract data matching the requested instruction or schema.",
      "The target data was not visible or not present on the page.",
    ],
    solutions: [
      "Make the extraction instruction more specific.",
      "Add a wait before extraction if the target data loads asynchronously.",
    ],
  },
  [ErrorCodes.MISSING_FRAME_COMMANDS]: {
    friendlyError: "No platform commands were registered for this frame.",
    reasons: [
      "The platform did not finish loading commands for the frame.",
      "The frame context was recreated during navigation.",
    ],
    solutions: [
      "Retry execution after the application is fully loaded.",
      "Check whether the selected platform supports commands in this frame.",
    ],
  },
  [ErrorCodes.MISSING_STEP_RESOLVERS]: {
    friendlyError: "The step has no resolver data.",
    reasons: [
      "The step does not contain selectors or fallback paths needed to find the element.",
      "Resolver data was removed or not generated during recording.",
    ],
    solutions: [
      "Re-record the step to generate resolver data.",
      "Update the step selectors or enable resolver generation.",
    ],
  },
  [ErrorCodes.INVALID_ASSERTION_OPERATOR]: {
    friendlyError: "The assertion uses an unsupported comparison operator.",
    reasons: [
      "The condition contains an operator that the runner does not recognize.",
      "The script was created with a newer or incompatible condition definition.",
    ],
    solutions: [
      "Edit the condition and choose a supported comparison operator.",
      "Re-save the assertion step in the current application version.",
    ],
  },
  [ErrorCodes.INVALID_STEP_VALUE_CONDITION]: {
    friendlyError: "The condition references an invalid step value.",
    reasons: [
      "A condition expects a numeric step reference but received another value.",
      "The condition definition is incomplete or corrupted.",
    ],
    solutions: [
      "Edit the condition and select the referenced step again.",
      "Recreate the condition if the referenced step is missing.",
    ],
  },
  [ErrorCodes.ASSERTION_STEP_NOT_FOUND]: {
    friendlyError: "The assertion references a step that cannot be found.",
    reasons: [
      "The referenced step was deleted or moved outside the available script context.",
      "The assertion still points to an old step id.",
    ],
    solutions: [
      "Edit the assertion and select an existing step.",
      "Recreate the assertion after script structure changes.",
    ],
  },
  [ErrorCodes.UNSUPPORTED_STRING_CONDITION_VALUE]: {
    friendlyError: "The condition value type is not supported.",
    reasons: [
      "A variable-based condition received a string value where an id was expected.",
      "The condition definition does not match the selected value type.",
    ],
    solutions: [
      "Edit the condition and select the variable again.",
      "Recreate the condition if the value type cannot be corrected.",
    ],
  },
  [ErrorCodes.CONDITION_VARIABLE_NOT_FOUND]: {
    friendlyError: "The condition variable cannot be found.",
    reasons: [
      "The referenced variable was deleted or is unavailable in this execution context.",
      "The condition references the wrong variable scope.",
    ],
    solutions: [
      "Edit the condition and select an existing variable.",
      "Verify that the variable is available before this step runs.",
    ],
  },
  [ErrorCodes.UNSUPPORTED_CONDITION_TYPE]: {
    friendlyError: "The condition uses an unsupported value type.",
    reasons: [
      "The runner does not recognize the condition value type.",
      "The script contains condition data from an incompatible version.",
    ],
    solutions: [
      "Re-save the condition in the current application version.",
      "Recreate the condition with a supported value type.",
    ],
  },
  [ErrorCodes.AI_EXTRACT_DATA_NOT_FOUND]: {
    friendlyError: "AI could not find the requested extracted data.",
    reasons: [
      "The requested data is not visible on the page.",
      "The extraction schema or instruction does not match the page content.",
    ],
    solutions: [
      "Adjust the extraction instruction to describe the target data more clearly.",
      "Add a wait before extraction if the page content loads later.",
    ],
  },
  [ErrorCodes.AI_OBSERVE_RESULTS_NOT_FOUND]: {
    friendlyError: "AI did not find any actionable elements.",
    reasons: [
      "The requested action does not match visible page elements.",
      "The page was still loading when AI observation ran.",
    ],
    solutions: [
      "Make the action instruction more specific.",
      "Add a wait before the AI action step.",
    ],
  },
  [ErrorCodes.AI_OPERATOR_RESPONSE_INVALID]: {
    friendlyError: "AI returned an incomplete operator response.",
    reasons: [
      "The model response did not include the required planning fields.",
      "The response could not be mapped to a runner action.",
    ],
    solutions: [
      "Retry the step to request a new model response.",
      "Simplify the AI instruction if the task is ambiguous.",
    ],
  },
  [ErrorCodes.AI_OPERATOR_UNKNOWN_ACTION]: {
    friendlyError: "AI requested an unsupported action.",
    reasons: [
      "The model selected an action that is not available in the runner.",
      "The requested action is not provided by the current platform or tool set.",
    ],
    solutions: [
      "Retry the step or simplify the AI instruction.",
      "Check whether the platform exposes the action required by the instruction.",
    ],
  },
  [ErrorCodes.AI_RECOVERY_FAILED]: {
    friendlyError: "AI recovery failed.",
    reasons: [
      "The recovery model could not find a successful way to continue.",
      "The page state did not match the failing step context.",
    ],
    solutions: [
      "Review the failed step and update its selectors or timing.",
      "Retry recovery after confirming the application is in the expected state.",
    ],
  },
  [ErrorCodes.AI_RECOVERY_INCOMPLETE]: {
    friendlyError: "AI recovery did not complete successfully.",
    reasons: [
      "Recovery started but did not finish all required actions.",
      "The page state changed during recovery.",
    ],
    solutions: [
      "Retry recovery or update the failing step.",
      "Add waits around navigation or dynamic page updates.",
    ],
  },
  [ErrorCodes.AI_RECOVERY_DATA_ABORTED]: {
    friendlyError: "Recovery data execution was aborted.",
    reasons: [
      "The run was stopped while recovery data was executing.",
      "The recovery step was cancelled by the runner abort signal.",
    ],
    solutions: [
      "Retry the execution if it was stopped unintentionally.",
      "Check whether a timeout or manual stop interrupted recovery.",
    ],
  },
  [ErrorCodes.AI_RECOVERY_DATA_FAILED]: {
    friendlyError: "Saved recovery data failed.",
    reasons: [
      "The saved recovery action no longer matches the page.",
      "The page state changed since recovery data was generated.",
    ],
    solutions: [
      "Regenerate recovery data for this step.",
      "Update the step if the application flow changed.",
    ],
  },
  [ErrorCodes.SCRIPT_STEPS_NOT_FOUND]: {
    friendlyError: "Script steps could not be loaded for recovery.",
    reasons: [
      "The script root has no available steps.",
      "The runner could not retrieve steps for recovery context.",
    ],
    solutions: [
      "Verify that the script contains steps and is saved correctly.",
      "Retry execution after refreshing script data.",
    ],
  },
  [ErrorCodes.RECOVERY_STEP_NOT_FOUND]: {
    friendlyError: "The failed step could not be found for recovery.",
    reasons: [
      "The step was removed or changed while recovery was running.",
      "The execution log references a step that is no longer in the script.",
    ],
    solutions: [
      "Retry execution with the latest script version.",
      "Recreate recovery data after editing script steps.",
    ],
  },
  [ErrorCodes.VARIABLE_TO_OVERRIDE_NOT_FOUND]: {
    friendlyError: "The variable to override could not be found.",
    reasons: [
      "The referenced variable is missing from local and global scopes.",
      "The assign value step points to a variable that was deleted or changed.",
    ],
    solutions: [
      "Edit the step and select an existing variable.",
      "Create the variable before this step runs.",
    ],
  },
  [ErrorCodes.ELEMENT_NOT_FOUND]: {
    friendlyError: "The runner could not find the target element.",
    reasons: [
      "The player looked for the element, but the field was missing or its metadata changed.",
      "None of the recorded selectors matched an element on the page.",
      "The element was not visible or loaded before the lookup timeout ended.",
    ],
    solutions: [
      "Verify that the field is visible and that its metadata, such as XPath or other selectors, has not changed.",
      "Re-record or update the step selectors.",
      "Add a wait before this step if the element appears after loading or navigation.",
    ],
  },
  [ErrorCodes.ELEMENT_VALIDATION_FAILED]: {
    friendlyError: "The runner found an element, but it did not pass validation.",
    reasons: [
      "A selector matched the wrong element.",
      "The platform-specific validation rejected the matched element.",
    ],
    solutions: [
      "Update the selector so it points to the intended element.",
      "Check whether the page layout or platform validation rules changed.",
    ],
  },
  [ErrorCodes.TAB_CLOSED]: {
    friendlyError: "The tab has been closed.",
    reasons: [
      "The tab was closed while the step was running.",
      "The tab was closed by the user or the browser.",
      "The player page was closed manually, so the script stopped because it could no longer continue execution.",
    ],
    solutions: ["Retry the execution and keep the player page open until the script finishes."],
  },
  [ErrorCodes.SCRIPT_EXPIRED]: {
    friendlyError: "The script execution has expired.",
    reasons: [
      "The script execution timed out before completing all steps.",
      "The script was not designed to run for this long.",
    ],
    solutions: ["Retry the execution."],
  },
} satisfies Partial<Record<ErrorCodes, ErrorCodeDetails>>;

export type ApplicationVariantConfig = {
  // Metadata for runner layout
  runnerTitle: string;
  runnerDescription: string;
  runnerIcon: string;
  // Metadata for protected layout
  protectedTitle: string;
  protectedDescription: string;
  protectedIcon: string;
  // Extension configuration
  extensionIcon: string;
  extensionTitle: string;
  extensionName: string;
  extensionDescription: string;
  extensionErrorKey: "unableToOpenEAExtension" | "unableToOpenABExtension";
  extensionLogo: "LogoEA" | "LogoAB02" | "LogoEALite";
  extensionDevToolsTitle: string;
  extensionOverlayBackgroundImage: string;
  extensionOverlayButtonClass: string;
  // Theme configuration
  themeVariant: "EA" | "AB" | "EA_LITE";
};

export const applicationVariants: Record<ApplicationVariant, ApplicationVariantConfig> = {
  [ApplicationVariant.EA]: {
    runnerTitle: "Testing by XPLUS Runner",
    runnerDescription: "Testing by XPLUS Runner",
    runnerIcon: "/favicon.ico",
    protectedTitle: "Testing by XPLUS",
    protectedDescription: "Testing by XPLUS",
    protectedIcon: "/favicon.ico",
    extensionIcon: "icon-128.png",
    extensionTitle: "Testing by XPLUS Recorder",
    extensionName: "__MSG_extensionName__",
    extensionDescription: "__MSG_extensionDescription__",
    extensionErrorKey: "unableToOpenEAExtension",
    extensionLogo: "LogoEA",
    extensionDevToolsTitle: "Testing by XPLUS Recorder DevTools",
    extensionOverlayBackgroundImage: "/lp_bg_60.jpg",
    extensionOverlayButtonClass: "",
    themeVariant: "EA",
  },
  [ApplicationVariant.AB]: {
    runnerTitle: "AB Runner",
    runnerDescription: "Automated Boost Runner",
    runnerIcon: "/favicon-AB.ico",
    protectedTitle: "Automated Boost",
    protectedDescription: "Automated Boost",
    protectedIcon: "/favicon-AB.ico",
    extensionIcon: "icon-ab-128.png",
    extensionTitle: "Automated Boost Recorder",
    extensionName: "__MSG_extensionNameAB__",
    extensionDescription: "__MSG_extensionDescriptionAB__",
    extensionErrorKey: "unableToOpenABExtension",
    extensionLogo: "LogoAB02",
    extensionDevToolsTitle: "Automated Boost Recorder DevTools",
    extensionOverlayBackgroundImage: "/lp_bg_ab.jpg",
    extensionOverlayButtonClass: "ea-extension-open-panel-view-btn-ab",
    themeVariant: "AB",
  },
  [ApplicationVariant.EA_LITE]: {
    runnerTitle: "Testing Lite by XPLUS Runner",
    runnerDescription: "Testing Lite by XPLUS Runner",
    runnerIcon: "/favicon-EALite.ico",
    protectedTitle: "Testing Lite by XPLUS",
    protectedDescription: "Testing Lite by XPLUS",
    protectedIcon: "/favicon-EALite.ico",
    extensionIcon: "icon-128.png",
    extensionTitle: "Testing Lite by XPLUS Recorder",
    extensionName: "__MSG_extensionNameEALite__",
    extensionDescription: "__MSG_extensionDescriptionEALite__",
    extensionErrorKey: "unableToOpenEAExtension",
    extensionLogo: "LogoEALite",
    extensionDevToolsTitle: "Testing Lite by XPLUS Recorder DevTools",
    extensionOverlayBackgroundImage: "/lp_bg_60.jpg",
    extensionOverlayButtonClass: "",
    themeVariant: "EA",
  },
};
